/**
 * Live publications from ORCID's public API.
 *
 * No key, no backend: pub.orcid.org serves `access-control-allow-origin: *`, so
 * the browser fetches it directly.
 *
 * This ADDS to the curated list rather than replacing it, and that is the whole
 * design. Dr. Price's ORCID record currently holds three works, all 2022–2024,
 * while the hand-maintained list holds forty spanning 1999–2021 — swapping one
 * for the other would take the page from forty publications to three. Merged,
 * the record runs 1999–2024 and any work added to ORCID from now on appears here
 * without anyone editing the site.
 *
 * Everything here is best-effort. If ORCID is slow, down, or changes shape, the
 * page still renders the curated list; a publications page that fails because a
 * third party is having a bad day is worse than one that is merely not live.
 */

const API = "https://pub.orcid.org/v3.0";

/** ORCID iDs are 16 digits in four groups; anything else we refuse to call with. */
export function isValidOrcid(id) {
  return /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(String(id || "").trim());
}

const json = async (url, signal) => {
  const res = await fetch(url, { headers: { Accept: "application/json" }, signal });
  if (!res.ok) throw new Error(`ORCID ${res.status}`);
  return res.json();
};

const doiOf = (work) => {
  const ids = work?.["external-ids"]?.["external-id"] || [];
  const doi = ids.find((e) => e?.["external-id-type"] === "doi");
  return doi?.["external-id-value"]?.toLowerCase().trim() || null;
};

/** DOIs are compared case-insensitively and without their resolver prefix. */
export function normaliseDoi(value) {
  const s = String(value || "").toLowerCase().trim();
  if (!s) return null;
  const m = s.match(/10\.\d{4,9}\/\S+/);
  return m ? m[0].replace(/[).,;]+$/, "") : null;
}

const yearOf = (work) => {
  const y = work?.["publication-date"]?.year?.value;
  const n = parseInt(y, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * "Gillis, J.D., Price, G.W." from ORCID's contributor list.
 * ORCID stores a display name per contributor and nothing more dependable, so
 * this passes the credit-name through rather than trying to re-format initials.
 */
const authorsOf = (work) => {
  const list = work?.contributors?.contributor || [];
  return list
    .map((c) => c?.["credit-name"]?.value)
    .filter(Boolean)
    .join(", ");
};

/** Shape an ORCID work like the curated records so the page renders one list. */
function toPublication(work) {
  const doi = doiOf(work);
  const url = work?.url?.value || (doi ? `https://doi.org/${doi}` : "");
  return {
    id: `orcid-${work?.["put-code"]}`,
    title: work?.title?.title?.value || "Untitled",
    authors: authorsOf(work),
    venue: work?.["journal-title"]?.value || "",
    year: yearOf(work),
    abstract: work?.["short-description"] || "",
    external_url: url,
    file_path: "",
    doi,
    source: "orcid",
  };
}

/**
 * Every public work on a record, with contributors.
 *
 * Two calls, not one per work: the summaries endpoint has no author list, and
 * the bulk endpoint takes up to 100 put-codes at a time — so a record of any
 * realistic size costs a handful of requests rather than one per paper.
 */
export async function fetchOrcidWorks(orcidId, { signal } = {}) {
  if (!isValidOrcid(orcidId)) throw new Error("Not a valid ORCID iD");

  const summary = await json(`${API}/${orcidId}/works`, signal);
  const putCodes = (summary?.group || [])
    .map((g) => g?.["work-summary"]?.[0]?.["put-code"])
    .filter(Boolean);
  if (putCodes.length === 0) return [];

  const CHUNK = 100;
  const works = [];
  for (let i = 0; i < putCodes.length; i += CHUNK) {
    const batch = putCodes.slice(i, i + CHUNK).join(",");
    // A failed batch loses that batch, not the whole record.
    try {
      const detail = await json(`${API}/${orcidId}/works/${batch}`, signal);
      for (const entry of detail?.bulk || []) {
        if (entry?.work) works.push(toPublication(entry.work));
      }
    } catch (err) {
      if (err?.name === "AbortError") throw err;
    }
  }
  return works;
}

/**
 * Curated entries first, ORCID filling the gaps.
 *
 * The curated record wins any collision: it is checked by a person, and its
 * venue strings carry volume and page numbers that ORCID does not hold. Matching
 * is by DOI where both sides have one, falling back to a normalised title so a
 * hand-entered record without a DOI still suppresses its ORCID twin.
 */
export function mergePublications(curated = [], fromOrcid = []) {
  const seenDoi = new Set();
  const seenTitle = new Set();
  const titleKey = (t) =>
    String(t || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  for (const p of curated) {
    const doi = normaliseDoi(p.external_url) || normaliseDoi(p.doi);
    if (doi) seenDoi.add(doi);
    seenTitle.add(titleKey(p.title));
  }

  const additions = fromOrcid.filter((p) => {
    const doi = normaliseDoi(p.doi) || normaliseDoi(p.external_url);
    if (doi && seenDoi.has(doi)) return false;
    if (seenTitle.has(titleKey(p.title))) return false;
    if (doi) seenDoi.add(doi);
    seenTitle.add(titleKey(p.title));
    return true;
  });

  // Newest first. Entries with no year sort to the end rather than to year zero.
  return [...curated, ...additions].sort((a, b) => {
    const ya = Number.isFinite(a.year) ? a.year : -Infinity;
    const yb = Number.isFinite(b.year) ? b.year : -Infinity;
    if (ya !== yb) return yb - ya;
    return (Number(a.display_order) || 0) - (Number(b.display_order) || 0);
  });
}

export default { fetchOrcidWorks, mergePublications, isValidOrcid, normaliseDoi };
